import { TaggedError } from "@repo/core/tagged-error";

import { CLI_ERROR_CODES } from "@/lib/shared/errors-constants";
import type { CliErrorDetails } from "@/lib/cli/cli-error-details";

/**
 * A failure the user should read as a message, not a stack trace.
 *
 * `exitCode` is part of the contract for scripts and agents: 1 for a plain
 * failure, 2 for bad usage, 3 for "the session needs a human" — so a caller
 * can branch on it without parsing text. `details` carries the same failure in
 * machine form; under `--json` it is what gets printed (see cli-output.ts).
 */
export class CliError extends TaggedError<"CliError"> {
  readonly exitCode: number;
  readonly details: CliErrorDetails;
  /**
   * A followed session parking on a question is a non-zero outcome AND a result
   * worth reading. Carried here so stdout stays ONE JSON document.
   */
  readonly result?: unknown;

  constructor(
    message: string,
    exitCode = 1,
    details: CliErrorDetails = {},
    result?: unknown,
  ) {
    super("CliError", message);
    this.exitCode = exitCode;
    this.details = details;
    if (result !== undefined) {
      this.result = result;
    }
  }

  /** The code to report, defaulting to whatever the exit code implies. */
  get code(): string {
    if (this.details.code) {
      return this.details.code;
    }
    if (this.exitCode === 2) {
      return CLI_ERROR_CODES.BAD_USAGE;
    }
    if (this.exitCode === 3) {
      return CLI_ERROR_CODES.NEEDS_HUMAN;
    }
    return CLI_ERROR_CODES.ERROR;
  }
}
